import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import posthog, { POSTHOG_GROUP_TYPES, setPosthogGroups } from './posthog';

export const APP_EVENTS = {
  LOGIN_SUCCEEDED: 'login_succeeded',
  LOGIN_FAILED: 'login_failed',
  PASSWORD_RESET: 'password_reset',
  USER_LOGGED_OUT: 'user_logged_out',
  ORDER_SELECTED: 'order_selected',
  PATIENT_INFORMATION_CONFIRMED: 'patient_information_confirmed',
  ORDER_CREATED: 'order_created',
  PAGE_CHANGED: 'page_changed',
  SEARCH_FILTER_APPLIED: 'search_filter_applied',
  ORDERS_LOADED: 'orders_loaded',
  LOCAL_ORDERS_PRUNED: 'local_orders_pruned',
  TABLE_CONFIG_CHANGED: 'table_config_changed',
  REPORT_STEP_COMPLETED: 'report_step_completed',
  ORDER_MARKED_AS_COMPLETE: 'order_marked_as_complete',
  ORDER_CANCELLED: 'order_cancelled',
  CAMERA_CONNECTION_ATTEMPTED: 'camera_connection_attempted',
  CAMERA_AUTO_SCAN_INITIATED: 'camera_auto_scan_initiated',
  CAMERA_SCAN_COMPLETED: 'camera_scan_completed',
  CAMERA_MANUALLY_DISCONNECTED: 'camera_manually_disconnected',
  CAMERA_VOLUME_CHANGED: 'camera_volume_changed',
  CAMERA_BATTERY_LOW: 'camera_battery_low',
  CAMERA_STORAGE_LOW: 'camera_storage_low',
  CAMERA_LANGUAGE_CHANGED: 'camera_language_changed',
  CAMERA_CONNECTION_ISSUE: 'camera_connection_issue',
  APPLICATION_SETTING_CHANGED: 'application_setting_changed',
  USER_ADDED: 'user_added',
  USER_DELETED: 'user_deleted',
  USER_DETAILS_CHANGED: 'user_details_changed',
  ADMIN_RESET_USER_PASSWORD: 'admin_reset_user_password',
  USERS_LOADED: 'users_loaded',
} as const;

export const ORDER_SELECTION_SOURCE = {
  OPEN_ORDERS: 'open_orders',
  DUPLICATE_MATCH: 'duplicate_match',
} as const;
export type OrderSelectionSource =
  (typeof ORDER_SELECTION_SOURCE)[keyof typeof ORDER_SELECTION_SOURCE];

export const TABLE_CONTEXT = {
  OPEN_ORDERS: 'open_orders',
  RESULTS: 'results',
  STANDALONE_RESULTS: 'standalone_results',
  USERS: 'users',
} as const;
export type TableContext = (typeof TABLE_CONTEXT)[keyof typeof TABLE_CONTEXT];

export const REPORT_STEP = {
  SHARED_WITH_PROVIDER: 'shared_with_provider',
  SAVED_TO_CHART: 'saved_to_chart',
} as const;
export type ReportStep = (typeof REPORT_STEP)[keyof typeof REPORT_STEP];

export const MARK_COMPLETE_VIA = {
  REPORT: 'report',
} as const;
export type MarkCompleteVia =
  (typeof MARK_COMPLETE_VIA)[keyof typeof MARK_COMPLETE_VIA];

export const CAMERA_CONNECTION_METHOD = {
  AUTO: 'auto',
  MANUAL_SCAN: 'manual_scan',
  MANUAL_IP: 'manual_ip',
} as const;
export type CameraConnectionMethod =
  (typeof CAMERA_CONNECTION_METHOD)[keyof typeof CAMERA_CONNECTION_METHOD];

export const CAMERA_CONNECTION_OUTCOME = {
  SUCCESS: 'success',
  FAILURE: 'failure',
} as const;
export type CameraConnectionOutcome =
  (typeof CAMERA_CONNECTION_OUTCOME)[keyof typeof CAMERA_CONNECTION_OUTCOME];

export const APPLICATION_SETTING = {
  MFA_REQUIRED: 'mfa_required',
} as const;
export type ApplicationSetting =
  (typeof APPLICATION_SETTING)[keyof typeof APPLICATION_SETTING];

export const LOGIN_METHOD = {
  PASSWORD: 'password',
  OKTA: 'okta',
  MICROSOFT: 'microsoft',
  MFA: 'mfa',
} as const;
export type LoginMethod = (typeof LOGIN_METHOD)[keyof typeof LOGIN_METHOD];

export const PASSWORD_RESET_VIA = {
  FORGOT: 'forgot',
  PROFILE: 'profile',
} as const;
export type PasswordResetVia =
  (typeof PASSWORD_RESET_VIA)[keyof typeof PASSWORD_RESET_VIA];

export const LOGOUT_TRIGGER = {
  USER: 'user',
  IDLE: 'idle',
} as const;
export type LogoutTrigger =
  (typeof LOGOUT_TRIGGER)[keyof typeof LOGOUT_TRIGGER];

export const EXAM_EVENTS = {
  EXAM_STARTED: 'exam_started',
  EXAM_RESUMED: 'exam_resumed',
  EXAM_PAUSED: 'exam_paused',
  EXAM_CANCELLED: 'exam_cancelled',
  EXAM_COMPLETED: 'exam_completed',
  EXAM_SUBMITTED_ANYWAY: 'exam_submitted_anyway',
  EXAM_SAVED_AND_DILATED: 'exam_saved_and_dilated',
  DIABETIC_DATA_COLLECTED: 'diabetic_data_collected',
  IMAGING_START_REQUESTED: 'imaging_start_requested',
  IMAGING_STARTED: 'imaging_started',
  IMAGING_CANCEL_REQUESTED: 'imaging_cancel_requested',
  IMAGING_CANCELLED: 'imaging_cancelled',
  IMAGE_RETRIEVED: 'image_retrieved',
  IMAGE_UPLOADED: 'image_uploaded',
  IMAGE_UPLOAD_FAILED: 'image_upload_failed',
  RETAKE_REQUESTED: 'retake_requested',
  EXAM_ANALYSIS_STARTED: 'exam_analysis_started',
  EXAM_ANALYSIS_RECEIVED: 'exam_analysis_received',
  EXAM_ANALYSIS_FAILED: 'exam_analysis_failed',
  CAMERA_CONNECTION_ISSUE: 'camera_connection_issue',
  CAMERA_CONNECTION_RETRY_INITIATED: 'camera_connection_retry_initiated',
  CAMERA_CONNECTION_RETRIED: 'camera_connection_retried',
  EXAM_PAUSE_INITIATED: 'exam_pause_initiated',
} as const;

export enum CameraConnectionIssueReason {
  CANT_GET_IMAGE = 'cant_get_image',
  LOST_HEARTBEAT = 'lost_heartbeat',
  COMMAND_FAILED = 'command_failed',
}

export enum ExamPauseSource {
  CAMERA_CONNECTION = 'camera_connection',
}

export const RETAKE_PHASE = {
  PRE_ANALYSIS: 'pre_analysis',
  POST_ANALYSIS: 'post_analysis',
} as const;
export type RetakePhase = (typeof RETAKE_PHASE)[keyof typeof RETAKE_PHASE];

export const EXAM_STEP = {
  DIABETES_DATA: 'diabetes_data',
  PATIENT_POSITION: 'patient_position',
  IMAGING: 'imaging',
  ANALYSIS: 'analysis',
} as const;
export type ExamStep = (typeof EXAM_STEP)[keyof typeof EXAM_STEP];

export const NETWORK_TRANSPORT = {
  LAN: 'lan',
  INTERNET: 'internet',
} as const;
export type NetworkTransport =
  (typeof NETWORK_TRANSPORT)[keyof typeof NETWORK_TRANSPORT];

function isDev(): boolean {
  try {
    return Boolean(import.meta.env?.DEV);
  } catch {
    return false;
  }
}

// Module-scope timestamps for relative measurements.
let examStartedAt: number | null = null;
let analysisReceivedAt: number | null = null;

export function markExamStarted(): void {
  examStartedAt = performance.now();
  analysisReceivedAt = null;
}

export function markAnalysisReceived(): void {
  analysisReceivedAt = performance.now();
}

export function secondsSince(start: number): number {
  const elapsedSeconds = (performance.now() - start) / 1000;
  // Round to 2 decimal places
  return Math.round(elapsedSeconds * 100) / 100;
}

export function getSecondsSinceExamStarted(): number | undefined {
  return examStartedAt != null ? secondsSince(examStartedAt) : undefined;
}

export function getSecondsSinceAnalysisReceived(): number | undefined {
  return analysisReceivedAt != null
    ? secondsSince(analysisReceivedAt)
    : undefined;
}

async function buildExamProps(): Promise<Record<string, unknown>> {
  const {
    session,
    leftImageAttempts,
    rightImageAttempts,
    leftImageSubmission,
    rightImageSubmission,
  } = useRemoteBoundStore.getState();
  const props: Record<string, unknown> = {
    order_id: session.orderId,
    exam_id: session.iid,
    left_attempt_index: leftImageAttempts,
    right_attempt_index: rightImageAttempts,
    left_submission_index: leftImageSubmission,
    right_submission_index: rightImageSubmission,
  };
  const elapsed = getSecondsSinceExamStarted();
  if (elapsed !== undefined) {
    props.since_exam_started_seconds = elapsed;
  }
  return props;
}

function buildCameraProps(): Record<string, unknown> {
  const { cameraData } = useRemoteBoundStore.getState();
  const props: Record<string, unknown> = {};
  if (!cameraData) {
    return props;
  }
  props.camera_serial_number = cameraData.deviceSN;
  props.camera_version = cameraData.versionInfo.version;
  props.camera_side_gain = cameraData.sideGain.data;
  props.camera_main_gain = cameraData.mainGain.data;
  return props;
}

export async function captureExamEvent(
  name: string,
  props: Record<string, unknown> = {},
): Promise<void> {
  try {
    posthog.capture(name, { ...(await buildExamProps()), ...props });
  } catch (err) {
    if (isDev()) throw err;
    console.error('[posthogEvents] capture failed', err);
  }
}

export function captureAppEvent(
  name: string,
  props: Record<string, unknown> = {},
): void {
  try {
    posthog.capture(name, props);
  } catch (err) {
    if (isDev()) throw err;
    console.error('[posthogEvents] capture failed', err);
  }
}

export function reportCameraConnectionIssue(isConnectionLost: boolean): void {
  captureAppEvent(APP_EVENTS.CAMERA_CONNECTION_ISSUE, {
    reason: isConnectionLost ? 'connection_lost' : 'not_connected',
  });
}

export async function captureCameraEvent(
  name: string,
  props: Record<string, unknown> = {},
): Promise<void> {
  try {
    posthog.capture(name, {
      ...(await buildExamProps()),
      ...buildCameraProps(),
      ...props,
    });
  } catch (err) {
    if (isDev()) throw err;
    console.error('[posthogEvents] capture failed', err);
  }
}

// Sends `error` to PostHog's error-tracking surface
// (capture_exceptions is enabled in src/services/posthog.ts) with the same
// exam-correlation props every domain event carries, plus any caller context.
// Lets us match stack traces back to the exam_id / order_id and filter the
// error-tracking view by step, side, etc.
export async function captureExamException(
  error: unknown,
  context: Record<string, unknown> = {},
): Promise<void> {
  try {
    const errObj = error instanceof Error ? error : new Error(String(error));
    posthog.captureException(errObj, {
      ...(await buildExamProps()),
      ...context,
    });
  } catch (err) {
    if (isDev()) throw err;
    console.error('[posthogEvents] captureException failed', err);
  }
}

export function clearExamScopedGroups(): void {
  setPosthogGroups({
    [POSTHOG_GROUP_TYPES.ORDER]: undefined,
    [POSTHOG_GROUP_TYPES.SITE]: undefined,
  });
}

export type TimingProps = {
  duration_seconds: number;
  payload_bytes?: number;
  effective_connection_type?: string;
  downlink_mbps?: number;
  rtt_ms?: number;
  save_data?: boolean;
};

type NetworkInformation = {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
};

function readNavigatorConnection(): NetworkInformation | undefined {
  try {
    const nav = navigator as Navigator & { connection?: NetworkInformation };
    return nav.connection;
  } catch {
    return undefined;
  }
}

// Times an async call and returns its result plus timing properties suitable
// for inclusion in an analytics event. NETWORK_TRANSPORT.LAN returns duration
// + optional payload size only (camera/USB hop — internet metrics don't
// apply). NETWORK_TRANSPORT.INTERNET additionally snapshots
// navigator.connection — Chromium's own heuristic estimate of throughput and
// RTT, not a value we measure ourselves.
export async function withNetworkTiming<T>(
  fn: () => Promise<T>,
  opts: {
    transport: NetworkTransport;
    payloadBytes?: number | (() => number | undefined);
  },
): Promise<{ result: T; timingProps: TimingProps }> {
  const conn =
    opts.transport === NETWORK_TRANSPORT.INTERNET
      ? readNavigatorConnection()
      : undefined;
  const start = performance.now();
  const result = await fn();
  const duration_seconds = secondsSince(start);

  const payload_bytes =
    typeof opts.payloadBytes === 'function'
      ? opts.payloadBytes()
      : opts.payloadBytes;

  const timingProps: TimingProps = { duration_seconds };
  if (payload_bytes != null) timingProps.payload_bytes = payload_bytes;
  if (conn) {
    if (conn.effectiveType) {
      timingProps.effective_connection_type = conn.effectiveType;
    }
    if (typeof conn.downlink === 'number') {
      timingProps.downlink_mbps = conn.downlink;
    }
    if (typeof conn.rtt === 'number') timingProps.rtt_ms = conn.rtt;
    if (typeof conn.saveData === 'boolean') {
      timingProps.save_data = conn.saveData;
    }
  }
  return { result, timingProps };
}
